import { el, clear } from '../../utils/dom.js';
import { Reports, Rentals } from '../../api/resources.js';
import { fmtMoney, fmtDateTime, fmtNumber, fromNow } from '../../utils/format.js';
import { dataTable } from '../../components/table.js';
import { statusBadge } from '../../components/badge.js';
import { donut, donutLegend } from '../../components/charts.js';
import { skeletonStats } from '../../components/skeleton.js';
import { err } from '../../components/toast.js';

import { auth } from '../../store/auth.js';
import { navigate } from '../../router/index.js';

export default async function DashboardPage(outlet) {
  // El asistente empieza en lo que le toca hacer.
  if ((auth.user || {}).role === 'tecnico') { navigate('/mis-entregas'); return; }
  clear(outlet);

  const head = el('div', { class: 'page-head' },
    el('div', {},
      el('h1', {}, 'Dashboard'),
      el('div', { class: 'subtitle' }, fmtDateTime(new Date())),
    ),
    el('div', { class: 'flex gap-2 wrap' },
      quickAction('+ Renta',     '#/rentals/new', 'primary'),
      quickAction('+ Prestamo',  '#/loans/new'),
      quickAction('+ Reserva',   '#/reservations/new'),
      quickAction('+ Item',      '#/inventory/new'),
    ),
  );
  outlet.appendChild(head);

  // Rentas de hoy (solo si la empresa renta equipos).
  const rentasHost = el('div');
  outlet.appendChild(rentasHost);
  Rentals.config().then((cfg) => (cfg.modo === 'rental' ? Rentals.panel().then((p) => rentasHost.replaceChildren(panelRentas(p, cfg.moneda))) : null))
    .catch(() => {});

  const statsSkeleton = skeletonStats(8);
  outlet.appendChild(statsSkeleton);

  let data;
  try {
    data = await Reports.dashboard();
  } catch (ex) {
    statsSkeleton.replaceWith(el('div', { class: 'empty' },
      el('div', { class: 'empty-icon' }, '⚠'),
      el('h3', {}, 'No se pudo cargar el dashboard'),
      el('p', {}, ex.message),
    ));
    err(ex.message);
    return;
  }

  const u = data.units_by_status || {};
  const kpis = el('div', { class: 'stats stagger' },
    kpi('Items registrados',  fmtNumber(data.items?.total || 0), '📦'),
    kpi('Disponibles',        fmtNumber(u.disponible || 0), '✓', 'success'),
    kpi('Prestamos activos',  fmtNumber(data.loans?.active || 0), '🤝', 'warn',
        data.loans?.overdue > 0 ? `${data.loans.overdue} vencidos` : null),
    kpi('Rentas activas',     fmtNumber(data.rentals?.active || 0), '💰', 'primary',
        data.rentals?.overdue > 0 ? `${data.rentals.overdue} vencidas` : null),
    kpi('Ingreso (30d)',      fmtMoney(data.rental_income_30d), '📈'),
    kpi('Deuda pendiente',    fmtMoney(data.pending_debt), '⏳',
        data.pending_debt > 0 ? 'danger' : null),
    kpi('Mantenimiento',      fmtNumber(u.mantenimiento || 0), '🔧'),
    kpi('Valor inventario',   fmtMoney(data.inventory_value), '◆'),
  );
  statsSkeleton.replaceWith(kpis);

  const alerts = collectAlerts(data);
  if (alerts.length > 0) {
    outlet.appendChild(el('div', {
      class: 'card mb-5',
      style: 'background: var(--warning-soft); border-color: var(--warning); padding: var(--sp-4) var(--sp-5);',
    },
      el('div', { class: 'flex items-center gap-3' },
        el('div', { style: 'font-size:22px' }, '⚠'),
        el('div', { class: 'flex-1' },
          el('div', { class: 'text-bold', style: 'color: var(--warning)' }, 'Atencion requerida'),
          el('div', { class: 'text-sm muted' }, alerts.join(' · ')),
        ),
      ),
    ));
  }

  const grid = el('div', { style: 'display: grid; grid-template-columns: 2fr 1fr; gap: var(--sp-5);' });

  const left = el('div', { class: 'flex-col gap-4' });
  left.appendChild(buildUnitsDonut(data.units_by_status || {}));
  left.appendChild(buildUpcoming(data.upcoming_returns || []));
  grid.appendChild(left);

  const right = el('div', { class: 'flex-col gap-4' });
  right.appendChild(buildLowStock(data.low_stock || []));
  right.appendChild(buildFinancialSummary(data));
  grid.appendChild(right);

  outlet.appendChild(grid);

  // Mobile: una sola columna
  const mq = window.matchMedia('(max-width: 900px)');
  function applyMobile() {
    grid.style.gridTemplateColumns = mq.matches ? '1fr' : '2fr 1fr';
  }
  applyMobile();
  mq.addEventListener('change', applyMobile);
}

function quickAction(label, href, kind = '') {
  return el('a', { href, class: 'btn ' + kind }, label);
}

function kpi(label, value, icon = '', kind = '', delta = null) {
  const colors = { success: 'var(--success)', warn: 'var(--warning)', danger: 'var(--danger)', primary: 'var(--primary)' };
  return el('div', { class: 'stat' },
    icon ? el('div', { class: 'icon' }, icon) : null,
    el('div', { class: 'label' }, label),
    el('div', { class: 'value', style: kind && colors[kind] ? `color: ${colors[kind]}` : '' }, value),
    delta ? el('div', { class: 'delta' }, delta) : null,
  );
}

function collectAlerts(data) {
  const out = [];
  if (data.loans?.overdue > 0)   out.push(`${data.loans.overdue} prestamo(s) vencido(s)`);
  if (data.rentals?.overdue > 0) out.push(`${data.rentals.overdue} renta(s) vencida(s)`);
  if (data.pending_debt > 0)     out.push(`${fmtMoney(data.pending_debt)} en deuda pendiente`);
  if ((data.low_stock || []).length > 0) out.push(`${data.low_stock.length} item(s) bajo stock minimo`);
  return out;
}

function buildUnitsDonut(byStatus) {
  const colorMap = {
    disponible: 'var(--success)', prestado: 'var(--warning)',
    rentado: 'var(--primary)',    reservado: 'var(--info)',
    mantenimiento: '#ea580c',     danado: 'var(--danger)',
    perdido: '#6b7280',           retirado: '#475569',
  };
  const segments = Object.entries(byStatus)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ label: k, value: v, color: colorMap[k] || 'var(--text-3)' }));
  const total = segments.reduce((a, s) => a + s.value, 0);

  const card = el('div', { class: 'card hover-lift' });
  card.appendChild(el('div', { class: 'card-head' },
    el('h3', {}, 'Unidades por estado'),
    el('a', { href: '#/inventory', class: 'btn ghost sm' }, 'Ver inventario →'),
  ));

  if (total === 0) {
    card.appendChild(el('div', { class: 'empty' },
      el('div', { class: 'empty-icon' }, '📦'),
      el('h3', {}, 'No hay unidades aun'),
      el('p', {}, 'Crea items serializados para empezar a rastrearlos.'),
      el('a', { href: '#/inventory/new', class: 'btn primary mt-3' }, 'Crear item'),
    ));
    return card;
  }

  card.appendChild(el('div', { style: 'display: grid; grid-template-columns: 160px 1fr; gap: var(--sp-5); align-items: center;' },
    donut({ segments, centerValue: total, centerLabel: 'unidades' }),
    donutLegend(segments),
  ));
  return card;
}

function buildUpcoming(rows) {
  const card = el('div', { class: 'card hover-lift' });
  card.appendChild(el('div', { class: 'card-head' },
    el('h3', {}, 'Proximas devoluciones (7 dias)'),
    el('span', { class: 'badge muted' }, `${rows.length}`),
  ));

  if (rows.length === 0) {
    card.appendChild(el('div', { class: 'empty', style: 'padding: var(--sp-5)' },
      el('div', { class: 'empty-icon' }, '✓'),
      el('h3', {}, 'Sin devoluciones esta semana'),
      el('p', {}, 'Todo tranquilo por ahora.'),
    ));
    return card;
  }

  card.appendChild(dataTable({
    columns: [
      { key: 'source', label: 'Tipo', render: (r) => statusBadge(r.source === 'loan' ? 'prestado' : 'rentado') },
      { key: 'code',   label: 'Codigo', render: (r) =>
        el('a', { href: `#/${r.source === 'loan' ? 'loans' : 'rentals'}/${r.id}`, class: 'text-mono text-bold' }, r.code) },
      { key: 'responsible', label: 'Para', render: (r) => r.responsible || '—' },
      { key: 'due', label: 'Vence', render: (r) => el('div', {},
        el('div', {}, fmtDateTime(r.due)),
        el('div', { class: 'text-xs muted' }, fromNow(r.due)),
      ) },
    ],
    rows,
  }));
  return card;
}

function buildLowStock(rows) {
  const card = el('div', { class: 'card hover-lift' });
  card.appendChild(el('div', { class: 'card-head' },
    el('h3', {}, 'Stock bajo'),
    rows.length > 0 ? el('span', { class: 'badge danger' }, `${rows.length}`) : null,
  ));

  if (rows.length === 0) {
    card.appendChild(el('div', { class: 'empty', style: 'padding: var(--sp-5)' },
      el('div', { class: 'empty-icon' }, '✓'),
      el('h3', {}, 'Stock OK'),
      el('p', {}, 'Sin items bajo el minimo.'),
    ));
    return card;
  }

  const list = el('div', { class: 'feed' });
  for (const r of rows) {
    list.appendChild(el('a', {
      href: `#/inventory/${r.id}`,
      class: 'feed-item',
      style: 'text-decoration:none; color:inherit',
    },
      el('div', { class: 'feed-icon', style: 'background: var(--danger-soft); color: var(--danger)' }, '↓'),
      el('div', { class: 'feed-body' },
        el('div', { class: 'text-bold truncate' }, r.name),
        el('div', { class: 'text-xs muted' }, `${r.available} disp · min ${r.min_stock}`),
      ),
    ));
  }
  card.appendChild(list);
  return card;
}

function buildFinancialSummary(data) {
  const card = el('div', { class: 'card hover-lift' });
  card.appendChild(el('div', { class: 'card-head' },
    el('h3', {}, 'Financiero'),
    el('a', { href: '#/reports', class: 'btn ghost sm' }, 'Ver reportes →'),
  ));
  card.appendChild(el('div', { class: 'flex-col gap-3' },
    miniRow('Ingreso 30 dias',  fmtMoney(data.rental_income_30d), 'success'),
    miniRow('Deuda pendiente',  fmtMoney(data.pending_debt), data.pending_debt > 0 ? 'danger' : 'muted'),
    miniRow('Valor inventario', fmtMoney(data.inventory_value), 'muted'),
  ));
  return card;
}

function miniRow(label, value, kind = 'muted') {
  const colors = { success: 'var(--success)', danger: 'var(--danger)', muted: 'var(--text)' };
  return el('div', {
    class: 'flex justify-between items-center',
    style: 'padding: 8px 0; border-bottom: 1px solid var(--border)',
  },
    el('div', { class: 'text-sm muted' }, label),
    el('div', { class: 'text-bold', style: `color: ${colors[kind] || 'var(--text)'}; font-size: 16px;` }, value),
  );
}

/** Lo del día y el dinero de las rentas. */
function panelRentas(p, moneda) {
  const $ = (n) => fmtMoney(n, moneda || 'DOP');
  const cuando = (iso) => new Date(iso).toLocaleString('es-DO', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'America/Santo_Domingo' });
  const pct = p.ocupacionHoy.total ? Math.round((p.ocupacionHoy.ocupado / p.ocupacionHoy.total) * 100) : 0;
  const listas = p.entregasHoy.filter((e) => e.lista).length;
  const enlace = (href, ...c) => el('a', { href, class: 'rp-link' }, ...c);
  const lista = (titulo, filas, vacio) => el('section', { class: 'card' },
    el('h3', { class: 'rp-h' }, titulo),
    filas.length ? el('ul', { class: 'rp-lista' }, ...filas) : el('p', { class: 'muted text-sm' }, vacio));
  return el('div', { class: 'mb-4' },
    el('div', { class: 'flex gap-2 wrap mb-3' },
      el('a', { href: '#/calendario', class: 'btn' }, 'Calendario'),
      el('a', { href: '#/calendario?vista=equipos', class: 'btn' }, 'Disponibilidad por equipo'),
      el('a', { href: '#/rentals/credito', class: 'btn' }, 'Crédito'),
    ),
    el('div', { class: 'stats stagger' },
      kpi('Salen hoy', String(p.entregasHoy.length), '↗', null, p.entregasHoy.length ? `${listas} lista${listas === 1 ? '' : 's'} para entregar` : 'Ninguna entrega'),
      kpi('Vuelven hoy', String(p.devolucionesHoy.length), '↙'),
      kpi('Atrasadas', String(p.atrasadas.length), '⚠', p.atrasadas.length ? 'danger' : 'success'),
      kpi('Por confirmar', String(p.porConfirmar), '○', p.porConfirmar ? 'warn' : null, 'Falta pago o crédito'),
      kpi('Cobrado este mes', $(p.cobradoMes), '💵', 'success'),
      kpi('Por cobrar', $(p.porCobrar), '⏳', p.porCobrar > 0 ? 'warn' : null),
      kpi('Depósitos en mano', $(p.depositosEnMano), '🔒'),
      kpi('Ocupación hoy', `${pct} %`, '▦', null, `${p.ocupacionHoy.ocupado} de ${p.ocupacionHoy.total} equipos`),
    ),
    p.atrasadas.length ? el('div', { class: 'rp-alerta mb-3' }, ...p.atrasadas.slice(0, 5).map((r) =>
      el('div', {}, enlace(`#/rentals/${r.id}`, `${r.code} · ${r.cliente} debía devolver el ${cuando(r.fin)}`)))) : null,
    el('div', { class: 'rp-tres-col' },
      lista('Hoy', [
        ...p.entregasHoy.map((e) => el('li', {}, enlace(`#/rentals/${e.id}`, el('strong', {}, `Sale ${e.hora}`), ` · ${e.code} · ${e.cliente}`),
          e.lista ? null : el('div', { class: 'text-xs muted' }, e.falta.join(' · ')))),
        ...p.devolucionesHoy.map((e) => el('li', {}, enlace(`#/rentals/${e.id}`, el('strong', {}, `Vuelve ${e.hora}`), ` · ${e.code} · ${e.cliente}`))),
      ], 'No sale ni vuelve nada hoy.'),
      lista('Próximas entregas', p.proximasEntregas.map((e) => el('li', { class: 'flex justify-between gap-2' },
        enlace(`#/rentals/${e.id}`, `${e.code} · ${e.cliente}`, e.etapa === 'pendiente' ? el('span', { class: 'text-xs rp-amarillo' }, ' por confirmar') : null),
        el('span', { class: 'text-xs muted' }, cuando(e.inicio)))), 'Nada en los próximos 7 días.'),
      lista('Próximas devoluciones', p.proximasDevoluciones.map((e) => el('li', { class: 'flex justify-between gap-2' },
        enlace(`#/rentals/${e.id}`, `${e.code} · ${e.cliente}`), el('span', { class: 'text-xs muted' }, cuando(e.fin)))), 'Nada en los próximos 7 días.'),
    ),
  );
}
